import React, { useCallback, useState } from 'react';
import Dropzone from 'react-dropzone';
import { toast } from 'react-toastify';
import { useObservable, useRetryableObservable } from 'react-use-observable';

import { CloudUploadOutlined } from '@ant-design/icons';

import Spin from 'components/Spin';
import MyModal from 'v2/components/Shared/MyModal';
import chargebackService from 'v2/services/chargeback';

import ChargebackDetailsListItem from './ListItem';

interface IProps {
  visible: boolean;
  invoiceId: number;
  retryLoadChargeback: () => void;
  setModalVisible: (visible: boolean, invoiceId: number | undefined) => void;
}

const ChargebackDetails = ({ setModalVisible, visible, invoiceId, retryLoadChargeback }: IProps) => {
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [hasUploaded, setHasUploded] = useState(false);
  const [chargebackFiles] = useObservable(() => chargebackService.getFiles(), []);

  const [isLoading] = useObservable(() => chargebackService.isLoadingFiles(), []);

  const [, , , retry] = useRetryableObservable(() => chargebackService.loadChargebacksFiles(invoiceId), [invoiceId]);

  const setUpload = useCallback(() => setHasUploded(true), []);

  const handleClose = useCallback(() => {
    if (hasUploaded) {
      retryLoadChargeback();
    }

    setModalVisible(false, undefined);
  }, [hasUploaded, retryLoadChargeback, setModalVisible]);

  const onDrop = async (filesReceived: File[]) => {
    setLoadingSubmit(true);

    try {
      await Promise.all(
        filesReceived.map(async (file: File) => {
          await chargebackService.uploadChargebackFile(file, invoiceId);
        })
      );
      toast.success('Arquivo(s) adicionado(s) com sucesso!');
      setUpload();
      setLoadingSubmit(false);
      retry();
    } catch (error) {
      setLoadingSubmit(false);
      toast.error(error);
    }
  };

  const content = () => {
    return (
      <div>
        {loadingSubmit ? (
          <div>
            <Spin />
            <p>Por favor, aguarde. Estamos fazendo upload do(s) arquivo(s)...</p>
          </div>
        ) : (
          <div>
            <Dropzone onDrop={onDrop}>
              {({ getRootProps, getInputProps }) => (
                <section>
                  <div
                    className='flex w-full cursor-pointer items-center justify-between border border-dashed border-gray-200 p-4'
                    {...getRootProps()}
                  >
                    <input {...getInputProps()} />
                    <CloudUploadOutlined size={30} className='mr-8 ml-4 text-2xl' />
                    <div className='flex-1'>
                      <p className='mt-0! mb-1!'>Clique ou arraste os arquivos nessa área.</p>
                      <p className='mt-0! mb-0!'>Pode ser selecionado vários arquivos ao mesmo tempo!</p>
                    </div>
                  </div>
                </section>
              )}
            </Dropzone>
          </div>
        )}
      </div>
    );
  };

  if (!visible && invoiceId === undefined) return null;

  return (
    <MyModal
      title='Provas de acesso ao conteúdo'
      opened={visible}
      showOk={false}
      buttonCancel='Fechar'
      onClose={handleClose}
      submitDisabled={true}
    >
      {content()}
      {isLoading ? (
        <div className='flex items-center justify-center p-8'>
          <Spin size='large' message='Carregando arquivo(s) ...' />
        </div>
      ) : (
        <div className='max-h-87.5 overflow-y-auto'>
          {chargebackFiles?.map(item => (
            <ChargebackDetailsListItem key={item.id} setUpload={setUpload} invoiceId={invoiceId} data={item} />
          ))}
        </div>
      )}
    </MyModal>
  );
};

export default ChargebackDetails;
